// src/components/sponsor/dashboard/SponsorDashboard.jsx
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fetchSponsorableEvents, fetchMyInterests } from "@/store/slices/sponsorSlice";
import { Calendar, Heart, CheckCircle, Clock, ArrowRight, Search } from "lucide-react";
import StatCard from "@/components/common/StatCard";
import Button from "@/components/common/Button";
import Badge from "@/components/common/Badge";
import { getInterestStatusBadgeConfig } from "@/utils/constants";

const SponsorDashboard = () => {
  const dispatch = useDispatch();
  const { sponsor, events, interests, isLoading } = useSelector((state) => state.sponsor);

  useEffect(() => {
    dispatch(fetchSponsorableEvents({ limit: 5 }));
    dispatch(fetchMyInterests());
  }, [dispatch]);

  // Calculate stats
  const totalInterests = interests.length;
  const pendingInterests = interests.filter(i => i.status === "pending").length;
  const acceptedInterests = interests.filter(i => i.status === "accepted").length;
  const availableEvents = events.length;

  const getInterestStatusBadge = (status) => {
    const c = getInterestStatusBadgeConfig(status);
    return <Badge tone={c.tone} size="sm" style={c.customStyle}>{c.label}</Badge>;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <motion.h1
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-100"
          >
            Welcome, {sponsor?.company_name || "Sponsor"}
          </motion.h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Explore events and manage your sponsorships
          </p>
        </div>
        <Link to="/sponsor/events">
          <Button className="bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700">
            <Search className="w-4 h-4 mr-2" />
            Browse Events
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <StatCard
            title="Available Events"
            value={availableEvents}
            icon={<Calendar className="w-6 h-6" />}
            iconBgColor="bg-teal-100 dark:bg-teal-900/30"
            iconColor="text-teal-600 dark:text-teal-400"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <StatCard
            title="My Interests"
            value={totalInterests}
            icon={<Heart className="w-6 h-6" />}
            iconBgColor="bg-pink-100 dark:bg-pink-900/30"
            iconColor="text-pink-600 dark:text-pink-400"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <StatCard
            title="Pending"
            value={pendingInterests}
            icon={<Clock className="w-6 h-6" />}
            iconBgColor="bg-amber-100 dark:bg-amber-900/30"
            iconColor="text-amber-600 dark:text-amber-400"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <StatCard
            title="Accepted"
            value={acceptedInterests}
            icon={<CheckCircle className="w-6 h-6" />}
            iconBgColor="bg-green-100 dark:bg-green-900/30"
            iconColor="text-green-600 dark:text-green-400"
          />
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Available Events */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              Available Events
            </h2>
            <Link
              to="/sponsor/events"
              className="text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 flex items-center gap-1"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {isLoading ? (
            <div className you="p-8 text-center text-gray-500">Loading...</div>
          ) : events.length === 0 ? (
            <div className="p-8 text-center">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No events available for sponsorship</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {events.slice(0, 5).map((event) => (
                <Link
                  key={event._id}
                  to={`/sponsor/events/${event._id}`}
                  className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-800 dark:text-gray-100 truncate">
                      {event.title}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {new Date(event.start_date).toLocaleDateString("en-IN", {
                        day: "numeric", month: "short"
                      })}
                      {event.location?.city && ` • ${event.location.city}`}
                    </p>
                  </div>
                  <span className="text-sm text-teal-600 dark:text-teal-400">
                    {event.sponsorship_components?.length || 0} components
                  </span>
                </Link>
              ))}
            </div>
          )}
        </motion.div>

        {/* Recent Interests */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              My Interests
            </h2>
            <Link
              to="/sponsor/interests"
              className="text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 flex items-center gap-1"
            >
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {interests.length === 0 ? (
            <div className="p-8 text-center">
              <Heart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 mb-4">No sponsorship interests yet</p>
              <Link to="/sponsor/events">
                <Button variant="outline" size="sm">Browse Events</Button>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {interests.slice(0, 5).map((interest) => {
                const event = interest.event || interest.event_id;
                return (
                <Link
                  key={interest._id}
                  to={`/sponsor/events/${event?._id}`}
                  className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-800 dark:text-gray-100 truncate">
                      {event?.title || "Event"}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {interest.interested_components?.length || 0} components selected
                    </p>
                  </div>
                  {getInterestStatusBadge(interest.status)}
                </Link>
              )})}
            </div>
          )}
        </motion.div>
      </div>
      
    </div>
  );
};

export default SponsorDashboard;
